import { useCallback, useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, KpiCard } from "@/components/Bits";
import { inputCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function ConsolidatedReports() {
  const { can } = useAuth();
  const [scope, setScope] = useState("business");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setData(null); setError("");
    try {
      const { data } = await api.get("/reports/consolidated", { params: { scope, from_date: from || undefined, to_date: to || undefined } });
      setData(data);
    } catch (e) { setError(apiError(e)); }
  }, [scope, from, to]);
  useEffect(() => { load(); }, [load]);

  if (!can("finance.reports.view")) return <NoAccess what="Consolidated reports" />;
  const t = data?.totals;

  return (
    <div data-testid="consolidated-page">
      <PageHeader overline="Reports" title="Consolidated Report"
        subtitle="Cross-module revenue and payouts across Hospital, Pharmacy, Lab and Doctor compensation. Switch to Group scope to roll up every linked business — each counted exactly once." />

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Scope</p>
          <div className="flex gap-2">
            {["business", "group"].map((s) => (
              <button key={s} onClick={() => setScope(s)} data-testid={`scope-${s}`}
                className={`rounded-full px-4 py-2 text-sm font-medium border ${scope === s ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-slate-300 text-slate-700 hover:border-slate-900"}`}>
                {s === "business" ? "This business" : "Linked group"}
              </button>
            ))}
          </div>
        </div>
        <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">From</span>
          <input type="date" className={`${inputCls} mt-1`} data-testid="report-from" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">To</span>
          <input type="date" className={`${inputCls} mt-1`} data-testid="report-to" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <button onClick={load} className={ghostBtnCls} data-testid="report-refresh"><BarChart3 className="h-4 w-4" /> Refresh</button>
      </div>

      {error && <ErrorState message={error} />}
      {!data ? <Loading /> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Gross Revenue" value={inr(t.gross_revenue)} tone="success" testid="kpi-gross" />
            <KpiCard label="Expenses" value={inr(t.expenses)} tone="warning" testid="kpi-expenses" />
            <KpiCard label="Doctor Payouts" value={inr(t.doctor_payouts)} tone="accent" hint="Compensation (not revenue)" testid="kpi-payouts" />
            <KpiCard label="Net" value={inr(t.net_revenue)} testid="kpi-net" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiCard label="Hospital Revenue" value={inr(t.hospital_revenue)} testid="kpi-hospital" />
            <KpiCard label="Pharmacy Revenue" value={inr(t.pharmacy_revenue)} testid="kpi-pharmacy" />
            <KpiCard label="Lab Revenue" value={inr(t.lab_revenue)} hint={`Collected ${inr(t.lab_collected)}`} testid="kpi-lab" />
          </div>

          <Table testid="consolidated-table"
            columns={["Business", "Type", "Hospital", "Pharmacy", "Lab", "Payouts", "Expenses", "Net"]}
            rows={data.businesses}
            empty={<p className="p-6 text-sm text-slate-500">No businesses in scope.</p>}
            renderRow={(b) => (
              <tr key={b.business_id} data-testid={`report-row-${b.business_id}`}>
                <Td className="font-medium text-slate-900">{b.name}</Td>
                <Td className="text-slate-600 capitalize">{String(b.business_type).replace(/_/g, " + ")}</Td>
                <Td className="text-slate-700">{inr(b.hospital_revenue)}</Td>
                <Td className="text-slate-700">{inr(b.pharmacy_revenue)}</Td>
                <Td className="text-slate-700">{inr(b.lab_revenue)}</Td>
                <Td className="text-[#EA580C]">{inr(b.doctor_payouts)}</Td>
                <Td className="text-slate-700">{inr(b.expenses)}</Td>
                <Td className="font-semibold text-slate-900">{inr(b.net)}</Td>
              </tr>
            )} />
          <p className="text-xs text-slate-400">{data.note}</p>
        </div>
      )}
    </div>
  );
}
